import * as React from "react";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import classnames from "classnames";

import {
    CircleIcon,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    domain,
    InPersonInterviewTypeBadge,
    Label,
    NotificationBellIcon,
    PhoneInterviewTypeBadge,
    RelodeTheme,
    Text,
    textWithLinks,
    VideoInterviewTypeBadge,
} from "../../";
import { CandidateInterviewDetailsDialogProps } from "..";
import {
    InterviewOptionsEnum,
    InterviewStatusEnum,
    TimeZone,
} from "../../domain";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            candidateName: {
                lineHeight: 1.5,
            },
            dateTimeDurationFlex: {
                display: "flex",
                columnGap: 8,
                [theme.breakpoints.down("sm")]: {
                    flexDirection: "column",
                },
            },
            jobName: {
                color: theme.relode.colors.backgroundText.main,
            },
            label: {
                marginTop: theme.spacing(3),
            },
            interviewStatusFlex: {
                marginTop: theme.spacing(1.5),
                display: "flex",
                flexDirection: "row",
            },
            interviewStatusTypeFlex: {
                display: "flex",
                alignItems: "center",
                marginLeft: theme.spacing(3),
            },
            circleIcon: {
                fontSize: "0.55em",
                margin: theme.spacing(0.1, 0.3, 0, 0),
            },
            statusCancelled: {
                color: theme.relode.colors.interviewStatus.cancelled,
            },
            statusScheduled: {
                color: theme.relode.colors.interviewStatus.scheduled,
            },
            statusCompleted: {
                color: theme.relode.colors.interviewStatus.completed,
            },
            interviewStatusLabel: {
                fontSize: 12,
                paddingLeft: theme.spacing(0.3),
            },
            interviewNotificationDetailsFlex: {
                marginLeft: theme.spacing(3),
                display: "flex",
                alignItems: "center",
            },
            interviewNotificationDetails: {
                fontSize: 12,
                color: theme.relode.colors.backgroundText.main,
                paddingLeft: theme.spacing(0.3),
            },
            interviewDetailsText: {
                minHeight: 120,
            },
            interviewTypeBadge: {
                display: "flex",
                alignItems: "center",
            },
        }),
    { name: "CandidateInterviewDetailsDialog" }
);

export const CandidateInterviewDetailsDialog = (
    props: CandidateInterviewDetailsDialogProps
) => {
    const classes = useStyles({});

    const {
        candidateInterview,
        jobName,
        onClose,
        handleSend,
        showNotificationOptions = true,
    } = props;

    const hasInterviewDatePassed =
        new Date(candidateInterview.startDateTime).getTime() < Date.now();

    const disableDialogActions =
        hasInterviewDatePassed ||
        candidateInterview.interviewStatusTypeId ===
            domain.InterviewStatusEnum.COMPLETED.id ||
        candidateInterview.interviewStatusTypeId ===
            domain.InterviewStatusEnum.CANCELED.id;

    const displayDuration = () => {
        let durationString = "";
        if (candidateInterview.durationHours > 0) {
            durationString += `${candidateInterview.durationHours}`;
            if (candidateInterview.durationHours === 1) {
                durationString += " hr";
            } else {
                durationString += " hrs";
            }
        }
        if (candidateInterview.durationMinutes > 0) {
            durationString += ` ${candidateInterview.durationMinutes} mins`;
        }
        if (
            candidateInterview.durationHours <= 0 &&
            candidateInterview.durationMinutes <= 0
        ) {
            durationString = "n/a";
        }
        return `(Duration: ${durationString})`;
    };

    const displayInterviewTypeBadge = () => (
        <div className={classes.interviewTypeBadge}>
            {candidateInterview.interviewTypeId ===
            InterviewOptionsEnum.IN_PERSON.id ? (
                <InPersonInterviewTypeBadge />
            ) : candidateInterview.interviewTypeId ===
              InterviewOptionsEnum.PHONE.id ? (
                <PhoneInterviewTypeBadge />
            ) : candidateInterview.interviewTypeId ===
              InterviewOptionsEnum.VIDEO.id ? (
                <VideoInterviewTypeBadge />
            ) : null}
        </div>
    );

    const displayInterviewStatusType = () => (
        <div className={classes.interviewStatusTypeFlex}>
            <CircleIcon
                className={classnames(
                    {
                        [classes.statusScheduled]:
                            candidateInterview.interviewStatusTypeId ===
                            InterviewStatusEnum.SCHEDULED.id,
                        [classes.statusCompleted]:
                            candidateInterview.interviewStatusTypeId ===
                            InterviewStatusEnum.COMPLETED.id,
                        [classes.statusCancelled]:
                            candidateInterview.interviewStatusTypeId ===
                            InterviewStatusEnum.CANCELED.id,
                    },
                    classes.circleIcon
                )}
            />
            <Text variant="subtitle2" className={classes.interviewStatusLabel}>
                {InterviewStatusEnum.fromId(
                    candidateInterview.interviewStatusTypeId
                ).label.toUpperCase()}
            </Text>
        </div>
    );

    const displayInterviewNotificationDetails = () => {
        if (
            candidateInterview.interviewStatusTypeId ===
                InterviewStatusEnum.SCHEDULED.id &&
            showNotificationOptions
        ) {
            if (
                !hasInterviewDatePassed &&
                candidateInterview.sendNotification
            ) {
                return (
                    <div className={classes.interviewNotificationDetailsFlex}>
                        <NotificationBellIcon />
                        <Text
                            variant="subtitle2"
                            className={classes.interviewNotificationDetails}
                        >
                            INTERVIEW REMINDERS SCHEDULED
                        </Text>
                    </div>
                );
            }
        }
    };

    return (
        <Dialog open>
            <DialogTitle title="interview details" onClose={onClose} />
            <DialogContent>
                <Text variant="h3" className={classes.candidateName}>
                    {candidateInterview.candidateFullName}
                </Text>
                <Text variant="h5" className={classes.jobName}>
                    {jobName}
                </Text>
                <Label label="date and time" className={classes.label}>
                    <Text variant="h6" className={classes.dateTimeDurationFlex}>
                        <div>
                            {candidateInterview.startDateTime.split(" ")[0]}
                            {" at "}
                            {candidateInterview.startDateTime
                                .split(" ")
                                .slice(1)
                                .join(" ")}{" "}
                            {
                                TimeZone.fromId(candidateInterview.timeZoneId)
                                    .abbreviation
                            }{" "}
                        </div>
                        <div>
                            {displayDuration()}
                        </div>
                    </Text>
                </Label>
                <div className={classes.interviewStatusFlex}>
                    {displayInterviewTypeBadge()}
                    {displayInterviewStatusType()}
                    {displayInterviewNotificationDetails()}
                </div>
                <Label label="interview details" className={classes.label}>
                    <Text className={classes.interviewDetailsText} preserveFormat={true}>
                        {candidateInterview.interviewDetails?.length > 0
                            ? textWithLinks(candidateInterview.interviewDetails)
                            : "no details were provided."}
                    </Text>
                </Label>
            </DialogContent>
            {showNotificationOptions ? (
                <DialogActions
                    submit={{
                        label: "send reminder",
                        onSubmit: handleSend,
                        disabled: disableDialogActions,
                    }}
                />
            ) : null }
        </Dialog>
    );
};
